import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

// ── types (mirror backend/app/features/commissions/schemas.py) ─────────────

export type Role =
  | "ae"
  | "bdr"
  | "sales_engineer"
  | "sales_manager"
  | "vp_sales"
  | "cro"
  | "referral_partner"
  | "partner_manager"
  | "csm"
  | "vp_cs"
  | "product";

export type Basis = "billed" | "profit" | "replacement_delta";
export type Archetype =
  | "new"
  | "plg"
  | "upsell"
  | "poupsell"
  | "replacement"
  | "reseller"
  | "po"
  | "license"
  | "referral";
export type AssignmentStatus = "active" | "pending_approval" | "ended";
export type PeriodStatus = "draft" | "locked";
export type LineStatus = "payable" | "held_unpaid_invoice" | "catchup" | "imported";

export const ROLE_LABEL: Record<Role, string> = {
  ae: "AE",
  bdr: "BDR",
  sales_engineer: "Sales Engineer",
  sales_manager: "Sales Manager",
  vp_sales: "VP of Sales",
  cro: "CRO",
  referral_partner: "Referral Partner",
  partner_manager: "Partner Manager",
  csm: "CSM",
  vp_cs: "VP of Customer Success",
  product: "Product Team",
};

export const ARCHETYPE_LABEL: Record<Archetype, string> = {
  new: "New Contract",
  plg: "PLG",
  upsell: "Upsell / Add-on",
  poupsell: "PO Upsell",
  replacement: "Replacement",
  reseller: "Reseller Agreement",
  po: "Purchase Order",
  license: "License Agreement",
  referral: "Referral",
};

export const BASIS_LABEL: Record<Basis, string> = {
  billed: "Billed amount",
  profit: "Profit (billed − cost)",
  replacement_delta: "Replacement delta",
};

export interface Payee {
  id: string;
  display_name: string;
  default_role: Role;
  user_id: string | null;
  user_email: string | null;
  hubspot_owner_id: string | null;
  is_partner: boolean;
  is_active: boolean;
  notes: string | null;
  assignment_count: number;
}

export interface PayeeIn {
  display_name: string;
  default_role: Role;
  user_id?: string | null;
  hubspot_owner_id?: string | null;
  is_partner?: boolean;
  is_active?: boolean;
  notes?: string | null;
}

export interface PlanRateCell {
  role: Role;
  contract_archetype: Archetype;
  rate_pct: number;
  window_months: number;
  basis: Basis;
}

/** A named, dated comp plan — the whole grid approved as one document. */
export interface CompPlan {
  id: string;
  name: string;
  effective_from: string;
  effective_to: string | null;
  status: "pending_approval" | "approved";
  approved_by_name: string | null;
  approved_at: string | null;
  is_current: boolean;
  notes: string | null;
  rates: PlanRateCell[];
}

export interface PlanCreateIn {
  name: string;
  effective_from: string;
  copy_from_plan_id?: string | null;
  notes?: string | null;
}

export interface PlanRateIn {
  role: Role;
  contract_archetype: Archetype;
  rate_pct: number;
  window_months?: number;
  basis?: Basis;
}

export interface Assignment {
  id: string;
  contract_id: string;
  account_id: string | null;
  quickbooks_id: string | null;
  company_name: string | null;
  contract_type_name: string | null;
  contract_archetype: Archetype | null;
  payee_id: string;
  payee_name: string;
  role: Role;
  rate_pct: number | null;
  fixed_monthly_amount: number | null;
  basis: Basis;
  replacement_baseline_mrr: number | null;
  item_scope: string[] | null;
  month_from: number;
  month_to: number;
  window_start: string | null;
  includes_p1: boolean;
  sign_date: string | null;
  first_billing_date: string | null;
  off_plan: boolean;
  status: AssignmentStatus;
  approved_by: string | null;
  approved_at: string | null;
  source: string;
  notes: string | null;
  plan_rate_pct: number | null;
}

export interface AssignmentIn {
  contract_id: string;
  account_id?: string | null;
  quickbooks_id?: string | null;
  company_name?: string | null;
  contract_type_name?: string | null;
  contract_archetype?: Archetype | null;
  payee_id: string;
  role: Role;
  rate_pct?: number | null;
  fixed_monthly_amount?: number | null;
  basis?: Basis;
  replacement_baseline_mrr?: number | null;
  item_scope?: string[] | null;
  month_from?: number;
  month_to?: number;
  window_start?: string | null;
  sign_date?: string | null;
  first_billing_date?: string | null;
  includes_p1_override?: boolean | null;
  notes?: string | null;
}

export interface ContractContext {
  contract_id: string;
  company_name: string | null;
  contract_type_name: string | null;
  contract_signed_at: string | null;
  billing_effective_from: string | null;
  account_id: string | null;
  quickbooks_id: string | null;
  closer_name: string | null;
  closer_hubspot_owner_id: string | null;
  commission_rate: number | null;
  commissions_payout_ends_at: string | null;
  replaced_contract_id: string | null;
  mrr_total: number | null;
  items: { item_name: string; monthly_amount: number }[];
}

export interface PeriodSummary {
  period: string;
  status: PeriodStatus;
  exists: boolean;
  computed_at: string | null;
  data_source: string | null;
  locked_at: string | null;
  locked_by_name: string | null;
  total_payable: number;
  total_held: number;
  total_catchup: number;
  line_count: number;
}

export interface PayoutLine {
  id: string;
  payee_id: string;
  payee_name: string;
  assignment_id: string | null;
  contract_id: string | null;
  company_name: string | null;
  invoice_document_id: string | null;
  item_name: string | null;
  month_index: string | null;
  base_amount: number;
  rate_pct: number | null;
  amount: number;
  status: LineStatus;
  held_from_period: string | null;
  source: string;
  notes: string | null;
}

export interface PayeeGroup {
  payee_id: string;
  payee_name: string;
  payable_total: number;
  catchup_total: number;
  held_total: number;
  lines: PayoutLine[];
}

export interface PeriodReport {
  period: string;
  status: PeriodStatus;
  exists: boolean;
  computed_at: string | null;
  data_source: string | null;
  locked_at: string | null;
  locked_by_name: string | null;
  can_admin: boolean;
  groups: PayeeGroup[];
  grand_payable: number;
  grand_catchup: number;
  grand_held: number;
}

export interface LedgerCell {
  month_index: string;
  period: string | null;
  amount: number;
}

export interface LedgerActual {
  period: string;
  month_index: string | null;
  amount: number;
  status: LineStatus;
  invoice_document_id: string | null;
  locked: boolean;
}

export interface LedgerPayee {
  payee_id: string;
  payee_name: string;
  role: Role | "";
  forecast: LedgerCell[];
  forecast_total: number;
  actuals: LedgerActual[];
  actual_total: number;
}

export interface ContractLedger {
  contract_id: string;
  company_name: string | null;
  payees: LedgerPayee[];
  assignments: Assignment[];
}

export interface UnpaidHold {
  payee_id: string;
  payee_name: string;
  contract_id: string | null;
  company_name: string | null;
  invoice_document_id: string | null;
  months_held: number;
  first_held_period: string;
  held_total: number;
}

// ── hooks ──────────────────────────────────────────────────────────────────

const KEY = "commissions";

export function useMyPayees() {
  return useQuery({
    queryKey: [KEY, "me"],
    queryFn: () => apiFetch<Payee[]>("/commissions/me"),
    staleTime: 300_000,
  });
}

export function usePayees(enabled = true) {
  return useQuery({
    queryKey: [KEY, "payees"],
    queryFn: () => apiFetch<Payee[]>("/commissions/payees"),
    enabled,
    staleTime: 60_000,
  });
}

export function useSavePayee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id?: string; body: Partial<PayeeIn> & { clear_user?: boolean } }) =>
      vars.id
        ? apiFetch<Payee>(`/commissions/payees/${vars.id}`, { method: "PATCH", body: vars.body })
        : apiFetch<Payee>("/commissions/payees", { method: "POST", body: vars.body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [KEY, "payees"] }),
  });
}

export function useDeletePayee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/commissions/payees/${id}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [KEY, "payees"] }),
  });
}

export function usePlans(enabled = true) {
  return useQuery({
    queryKey: [KEY, "plans"],
    queryFn: () => apiFetch<CompPlan[]>("/commissions/plans"),
    enabled,
    staleTime: 60_000,
  });
}

function usePlanMutation<V>(fn: (vars: V) => Promise<CompPlan>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: [KEY, "plans"] }),
  });
}

export function useCreatePlan() {
  return usePlanMutation((body: PlanCreateIn) =>
    apiFetch<CompPlan>("/commissions/plans", { method: "POST", body }),
  );
}

export function useSetPlanRate() {
  return usePlanMutation((vars: { planId: string; body: PlanRateIn }) =>
    apiFetch<CompPlan>(`/commissions/plans/${vars.planId}/rates`, {
      method: "PUT",
      body: vars.body,
    }),
  );
}

export function useApprovePlan() {
  return usePlanMutation((planId: string) =>
    apiFetch<CompPlan>(`/commissions/plans/${planId}/approve`, { method: "POST" }),
  );
}

export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) =>
      apiFetch<void>(`/commissions/plans/${planId}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [KEY, "plans"] }),
  });
}

// ── paid amounts (financials reconciliation) ───────────────────────────────

export interface PaidAmountRow {
  payee_id: string;
  payee_name: string;
  system_payable: number;
  amount_paid: number | null;
  variance: number | null;
  note: string | null;
  recorded_by_name: string | null;
  recorded_at: string | null;
}

export interface PaidAmounts {
  period: string;
  rows: PaidAmountRow[];
  total_system: number;
  total_paid: number;
  can_record: boolean;
}

export function usePaidAmounts(period: string, enabled = true) {
  return useQuery({
    queryKey: [KEY, "paid", period],
    queryFn: () => apiFetch<PaidAmounts>(`/commissions/periods/${period}/paid`),
    enabled: enabled && !!period,
  });
}

export function useRecordPaidAmount(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { payee_id: string; amount_paid: number; note?: string | null }) =>
      apiFetch<PaidAmounts>(`/commissions/periods/${period}/paid`, {
        method: "PUT",
        body,
      }),
    onSuccess: (data) => qc.setQueryData([KEY, "paid", period], data),
  });
}

export function useContractAssignments(contractId: string) {
  return useQuery({
    queryKey: [KEY, "assignments", contractId],
    queryFn: () =>
      apiFetch<Assignment[]>(`/commissions/contracts/${contractId}/assignments`),
    enabled: !!contractId,
  });
}

export interface ContractSearchHit {
  contract_id: string;
  company_name: string | null;
  company_name_dba: string | null;
  contract_type_name: string | null;
  contract_signed_at: string | null;
  billing_effective_from: string | null;
  closer_name: string | null;
  mrr_total: number | null;
}

export function useContractSearch(q: string) {
  return useQuery({
    queryKey: [KEY, "contract-search", q],
    queryFn: () =>
      apiFetch<ContractSearchHit[]>(
        `/commissions/contracts/search?q=${encodeURIComponent(q)}`,
      ),
    enabled: q.trim().length >= 3,
    staleTime: 60_000,
  });
}

export function useContractContext(contractId: string, enabled: boolean) {
  return useQuery({
    queryKey: [KEY, "context", contractId],
    queryFn: () => apiFetch<ContractContext>(`/commissions/contracts/${contractId}/context`),
    enabled: enabled && !!contractId,
    staleTime: 300_000,
    retry: false,
  });
}

export function useSaveAssignment(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id?: string; body: Partial<AssignmentIn> }) =>
      vars.id
        ? apiFetch<Assignment>(`/commissions/assignments/${vars.id}`, {
            method: "PATCH",
            body: vars.body,
          })
        : apiFetch<Assignment>("/commissions/assignments", { method: "POST", body: vars.body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "assignments", contractId] });
      void qc.invalidateQueries({ queryKey: [KEY, "ledger", contractId] });
      void qc.invalidateQueries({ queryKey: [KEY, "approvals"] });
    },
  });
}

export function useDeleteAssignment(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/commissions/assignments/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "assignments", contractId] });
      void qc.invalidateQueries({ queryKey: [KEY, "ledger", contractId] });
    },
  });
}

export function useApprovals(enabled = true) {
  return useQuery({
    queryKey: [KEY, "approvals"],
    queryFn: () => apiFetch<Assignment[]>("/commissions/approvals"),
    enabled,
  });
}

export function useApproveAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<Assignment>(`/commissions/assignments/${id}/approve`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "approvals"] });
      void qc.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function usePeriods() {
  return useQuery({
    queryKey: [KEY, "periods"],
    queryFn: () => apiFetch<PeriodSummary[]>("/commissions/periods"),
    staleTime: 60_000,
  });
}

export function usePeriodReport(period: string) {
  return useQuery({
    queryKey: [KEY, "report", period],
    queryFn: () => apiFetch<PeriodReport>(`/commissions/periods/${period}/report`),
    enabled: !!period,
  });
}

export function useRunPeriod(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<PeriodReport>(`/commissions/periods/${period}/run`, { method: "POST" }),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "report", period], data);
      void qc.invalidateQueries({ queryKey: [KEY, "periods"] });
      void qc.invalidateQueries({ queryKey: [KEY, "collections"] });
    },
  });
}

export function useLockPeriod(period: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<PeriodReport>(`/commissions/periods/${period}/lock`, { method: "POST" }),
    onSuccess: (data) => {
      qc.setQueryData([KEY, "report", period], data);
      void qc.invalidateQueries({ queryKey: [KEY, "periods"] });
    },
  });
}

export function useContractLedger(contractId: string) {
  return useQuery({
    queryKey: [KEY, "ledger", contractId],
    queryFn: () => apiFetch<ContractLedger>(`/commissions/contracts/${contractId}/ledger`),
    enabled: !!contractId,
  });
}

export function useCollections() {
  return useQuery({
    queryKey: [KEY, "collections"],
    queryFn: () => apiFetch<UnpaidHold[]>("/commissions/collections"),
    staleTime: 60_000,
  });
}

// ── formatting helpers ─────────────────────────────────────────────────────

/** Money to exactly 2 decimals — ledger figures must be reconcilable, so no
 *  $K compaction. Negative renders with an explicit leading minus sign. */
export function fmtMoney2(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return v < 0 ? `−$${abs}` : `$${abs}`;
}

export function fmtRate(rate: number | null | undefined): string {
  if (rate == null) return "—";
  const pct = rate * 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(2).replace(/\.?0+$/, "")}%`;
}

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "2026-05" → "May 2026". */
export function monthLabel(ym: string | null | undefined): string {
  if (!ym || ym.length < 7) return "—";
  const name = MONTH_NAMES[Number(ym.slice(5, 7)) - 1];
  return name ? `${name} ${ym.slice(0, 4)}` : ym;
}

/** Current calendar month as "YYYY-MM". */
export function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Previous calendar month as "YYYY-MM" — the default run target (you
 *  reconcile the month that just closed). */
export function previousMonth(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** The current month and the prior `count - 1` months, newest first. */
export function recentMonths(count = 18): string[] {
  const out: string[] = [];
  const d = new Date();
  let y = d.getFullYear();
  let m = d.getMonth();
  for (let i = 0; i < count; i++) {
    out.push(`${y}-${String(m + 1).padStart(2, "0")}`);
    m -= 1;
    if (m < 0) {
      m = 11;
      y -= 1;
    }
  }
  return out;
}

// ── contract coverage worklist ─────────────────────────────────────────────

export type Coverage = "unassigned" | "pending" | "partial" | "covered";

export const COVERAGE_LABEL: Record<Coverage, string> = {
  unassigned: "Nobody assigned",
  pending: "Pending approval",
  partial: "Missing a paid role",
  covered: "Fully assigned",
};

export interface CoveragePayee {
  assignment_id: string;
  payee_id: string;
  payee_name: string;
  role: Role;
  rate_pct: number | null;
  fixed_monthly_amount: number | null;
  status: AssignmentStatus;
  off_plan: boolean;
}

export interface ContractCoverage {
  contract_id: string;
  account_id: string | null;
  company_name: string | null;
  company_name_dba: string | null;
  contract_type_name: string | null;
  contract_archetype: Archetype | null;
  contract_signed_at: string | null;
  billing_effective_from: string | null;
  mrr_total: number | null;
  closer_name: string | null;
  deal_owner_email: string | null;
  bdr_owner_email: string | null;
  partner_source_detail: string | null;
  hubspot_deal_id: string | null;
  coverage: Coverage;
  assignment_count: number;
  payees: CoveragePayee[];
  missing_roles: Role[];
}

export interface CoverageSummary {
  contracts: number;
  unassigned: number;
  partial: number;
  pending: number;
  covered: number;
  mrr_total: number;
  mrr_unassigned: number;
  data_source: string;
}

export interface ContractCoverageList {
  summary: CoverageSummary;
  contracts: ContractCoverage[];
  plan_name: string | null;
  warning: string | null;
}

export function useContractCoverage(windowMonths = 13) {
  return useQuery({
    queryKey: [KEY, "coverage", windowMonths],
    queryFn: () =>
      apiFetch<ContractCoverageList>(
        `/commissions/contracts/coverage?window_months=${windowMonths}`,
      ),
    // The warehouse roster query is the slow part; keep it warm while the user
    // filters and sorts, which is all client-side.
    staleTime: 300_000,
  });
}

// ── recommendation ─────────────────────────────────────────────────────────

export interface PlanGridCell {
  role: Role;
  rate_pct: number;
  window_months: number;
  basis: Basis;
}

export interface RecommendedRow {
  role: Role;
  rate_pct: number | null;
  fixed_monthly_amount: number | null;
  basis: Basis;
  month_from: number;
  month_to: number;
  payee_id: string | null;
  payee_name: string | null;
  match_source: string | null;
  match_evidence: string | null;
  warnings: string[];
}

export interface UnpricedSignal {
  role: Role;
  payee_id: string | null;
  payee_name: string | null;
  person_name: string | null;
  source: string;
  evidence: string | null;
  reason: string;
}

export interface Recommendation {
  contract_id: string;
  company_name: string | null;
  contract_type_name: string | null;
  contract_archetype: Archetype | null;
  sign_date: string | null;
  first_billing_date: string | null;
  mrr_total: number | null;
  account_id: string | null;
  quickbooks_id: string | null;
  plan: {
    id: string;
    name: string;
    effective_from: string;
    effective_to: string | null;
  } | null;
  plan_grid: PlanGridCell[];
  recommended: RecommendedRow[];
  unpriced_signals: UnpricedSignal[];
  already_assigned_roles: Role[];
  baseline_unavailable: string | null;
  blockers: string[];
}

/** How a suggested payee was matched — shown so a solid hit reads differently
 *  from a guess. Mirrors ``recommend.SOURCE_LABELS``. */
export const MATCH_SOURCE_LABEL: Record<string, string> = {
  deal_owner_email: "HubSpot deal owner (by email)",
  closer_hubspot_id: "contract closer (by HubSpot id)",
  closer_name: "contract closer (by name)",
  hubspot_bdr: "HubSpot BDR on the deal",
  hubspot_partner: "HubSpot partner source",
  role_holder: "only active payee in this role",
  existing_assignment: "already assigned",
};

export function useRecommendation(contractId: string, enabled: boolean) {
  return useQuery({
    queryKey: [KEY, "recommendation", contractId],
    queryFn: () =>
      apiFetch<Recommendation>(`/commissions/contracts/${contractId}/recommendation`),
    enabled: enabled && !!contractId,
    staleTime: 300_000,
    retry: false,
  });
}

// ── bulk assignment ────────────────────────────────────────────────────────

export interface BulkAssignmentRowIn {
  payee_id: string;
  role: Role;
  rate_pct?: number | null;
  fixed_monthly_amount?: number | null;
  basis?: Basis | null;
  month_from: number;
  month_to: number;
  item_scope?: string[] | null;
  notes?: string | null;
}

export interface BulkAssignmentIn {
  contract_id: string;
  account_id?: string | null;
  quickbooks_id?: string | null;
  company_name?: string | null;
  contract_type_name?: string | null;
  contract_archetype?: Archetype | null;
  basis: Basis;
  replacement_baseline_mrr?: number | null;
  item_scope?: string[] | null;
  window_start?: string | null;
  sign_date?: string | null;
  first_billing_date?: string | null;
  includes_p1_override?: boolean | null;
  notes?: string | null;
  payees: BulkAssignmentRowIn[];
}

export function useBulkAssign(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkAssignmentIn) =>
      apiFetch<Assignment[]>(
        `/commissions/contracts/${contractId}/assignments:bulk`,
        { method: "POST", body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, "coverage"] });
      void qc.invalidateQueries({ queryKey: [KEY, "assignments", contractId] });
      void qc.invalidateQueries({ queryKey: [KEY, "ledger", contractId] });
      void qc.invalidateQueries({ queryKey: [KEY, "recommendation", contractId] });
      void qc.invalidateQueries({ queryKey: [KEY, "approvals"] });
    },
  });
}

/** "2026-08-21" → "21 Aug 2026" (compact, unambiguous in a dense table). */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso || iso.length < 10) return "—";
  const name = MONTH_NAMES[Number(iso.slice(5, 7)) - 1];
  return name ? `${iso.slice(8, 10)} ${name} ${iso.slice(0, 4)}` : iso;
}
